import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Loading, NewRentalAgreement } from '../../components';
import { useTripStoreUpdater } from '../../hooks';
import { StoreState } from '../../types/store.type';
import { RequiredActions, Reservation } from 'guest-data-types-ts-lib';

import tripService from '../../services/trip/tripService';
import ddLogger from '../../logging/ddLogger';
import { AppRoutes } from '../../routes';
import segmentService from '../../services/segment/segmentService';
import { fetchTripError, accessTokenExpired } from '../../store/actions';
import * as _ from 'lodash';

import '../../styles/_rental-agreement.scss';
import { AuthUtils } from '../../utils';
import { LegacyRentalAgreement } from '../../components/RentalAgreement/LegacyRentalAgreement';

export const RentalAgreement: React.FC = () => {
    useTripStoreUpdater(true);

    const trip = useSelector((store: StoreState) => store.trip);
    const { unit, stay, guests } = trip;
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [isCheckPoliciesDisabled, setIsCheckPoliciesDisabled] = useState(false);
    const [checkPolicies, setCheckPolicies] = useState(false);
    const history = useHistory();
    const dispatch = useDispatch();
    let requiredActions: RequiredActions | undefined;
    let reservation: Reservation | undefined;

    useEffect(() => {
        if (requiredActions && requiredActions.contract_agreement?.completed) {
            setIsSubmitDisabled(true);
            setIsCheckPoliciesDisabled(true);
            setCheckPolicies(true);
        }
    }, [requiredActions]);

    if (trip.isLoading) {
        return <Loading />;
    }

    requiredActions = trip.required_actions;
    reservation = trip.reservation;

    if (!requiredActions || !unit || !stay || !guests || !reservation) {
        return null;
    }

    const handleSubmit = async () => {
        try {
            if (AuthUtils.isAccessTokenSessionExpired()) {
                dispatch(accessTokenExpired(true));
                return;
            }

            setIsSubmitDisabled(true);
            setIsSubmitLoading(true);
            if (!reservation) {
                throw Error('reservation undefined');
            }

            await tripService.acceptTermsByReservationId(reservation.id);

            segmentService.trackEvent('Trip Agreement Signed');
            ddLogger.info(`Trip Agreement Signed. ReservationId: ${reservation.id}`, { reservation_id: reservation.id });

            history.push({
                pathname: AppRoutes.RESERVATION,
                state: {
                    isRentalAgreementUpdated: true,
                },
            });
        } catch (e) {
            ddLogger.error(`ERROR signing agreement: ${e.message}`);
            dispatch(fetchTripError(e));
        }
    };

    const handleChangeAgreement = (e: any) => {
        if (requiredActions && requiredActions.contract_agreement?.completed) {
            return;
        }

        setIsSubmitDisabled(!e.target.checked);
        setCheckPolicies(e.target.checked);
    };

    const handleOnPrintClick = () => {
        window.print();
    };
    const { contract_agreement: contractAgreement } = requiredActions;
    const isNewRentalAgreement =
        contractAgreement?.display_new_terms && contractAgreement?.new_terms && !_.isEmpty(contractAgreement.new_terms);

    return (
        <div>
            {isNewRentalAgreement ? (
                <NewRentalAgreement
                    isSubmitDisabled={isSubmitDisabled}
                    isSubmitLoading={isSubmitLoading}
                    isCheckPoliciesDisabled={isCheckPoliciesDisabled}
                    checkPolicies={checkPolicies}
                    handleSubmit={handleSubmit}
                    handleChangeAgreement={handleChangeAgreement}
                />
            ) : (
                <LegacyRentalAgreement
                    isSubmitDisabled={isSubmitDisabled}
                    isSubmitLoading={isSubmitLoading}
                    isCheckPoliciesDisabled={isCheckPoliciesDisabled}
                    checkPolicies={checkPolicies}
                    handleSubmit={handleSubmit}
                    handleChangeAgreement={handleChangeAgreement}
                    handleOnPrintClick={handleOnPrintClick}
                />
            )}
        </div>
    );
};
